// Logger utility for environment-based logging
// Sanitizes sensitive data in production to prevent security issues

const isDevelopment = process.env.NODE_ENV === 'development';
const enableProductionLogging = process.env.ENABLE_PRODUCTION_LOGGING === 'true';

// List of sensitive keys to redact in production
const sensitiveKeys = [
  'password', 'token', 'api_key', 'apiKey', 'secret',
  'client_secret', 'clientSecret', 'private_key', 'privateKey',
  'stripe', 'webhook', 'session_id', 'sessionId',
  'access_token', 'accessToken', 'refresh_token', 'refreshToken',
  'authorization', 'cookie', 'credit_card', 'cvv', 'ssn',
  // MAITE-539: tenant/user identifiers must not appear in production logs.
  // Matching is on lowercased keys, so cover both snake_case and camelCase forms.
  'organization_id', 'organizationid', 'user_id', 'userid'
];

// A STRING value is redacted on the same words, except the plain word
// "webhook": a cost source of `webhook` or a "[dub-webhook] ..." message names
// no secret, and redacting it hid every webhook log line in production. A
// webhook's secret material is caught by shape instead (isWebhookSecret).
// Object KEYS still redact on the full list above.
const sensitiveStringKeys = sensitiveKeys.filter(key => key !== 'webhook');

// A URL naming a hook, or a hook path on its own: Slack, Discord, Zapier and
// most providers carry the credential in the URL itself, often in the path.
const HOOK_PATH = /\/(?:web)?hooks?\/[^\s/]/i;
function hasHookUrl(lowerStr: string): boolean {
  // Inspect each whitespace-delimited run once. Retrying an unbounded suffix
  // at every :// makes compact arrays of ordinary URLs quadratic.
  for (const [part] of lowerStr.matchAll(/\S+/g)) {
    const scheme = part.indexOf('://');
    if (scheme !== -1 && part.indexOf('hook', scheme + 3) !== -1) return true;
  }
  return false;
}
// A string naming "webhook" is also a secret when it carries a credential word
// ("webhook signing key abc", "x-webhook-signature: sha256=..."), any URL or
// a relative path (including ./ and ../): a provider's endpoint need not
// contain "hook" ("Webhook endpoint: /incoming/abc123").
const WEBHOOK_CREDENTIAL_WORD = /\b(?:signing|signature|key|auth|bearer|credentials?)\b/;
// Serialized labels can precede/follow webhook or live in a nested field.
const WEBHOOK_CREDENTIAL_LABEL = /webhook_*(?:signing|signature|key|api_*key|auth|bearer|credentials?)|(?:signing(?:_*key)?|signature|key|api_*key|auth|bearer|credentials?)_*webhook|\b(?:api_*key|signing_*key)\b/i;
// Require a path token's start, rather than a slash inside [media/upload-complete].
const WEBHOOK_RELATIVE_PATH = /(?:^|[\s"'=:,(\[])(?:\.{1,2}\/|\/)[^\s/]/;

function isWebhookSecret(value: string, lowerStr: string): boolean {
  if (lowerStr.includes('whsec_')) return true; // a Stripe or Svix signing secret
  if (hasHookUrl(lowerStr) || HOOK_PATH.test(value)) return true;
  return lowerStr.includes('webhook') && (lowerStr.includes('://') || WEBHOOK_RELATIVE_PATH.test(value) || WEBHOOK_CREDENTIAL_WORD.test(lowerStr) || WEBHOOK_CREDENTIAL_LABEL.test(value));
}

// Sanitize sensitive data in production
function sanitizeData(data: any): any {
  if (isDevelopment) return data;
  
  if (typeof data === 'string') {
    // Check if string might contain sensitive info
    const lowerStr = data.toLowerCase();
    if (sensitiveStringKeys.some(key => lowerStr.includes(key)) || isWebhookSecret(data, lowerStr)) {
      return '[REDACTED]';
    }
    return data;
  }
  
  if (typeof data === 'object' && data !== null) {
    if (Array.isArray(data)) {
      return data.map(sanitizeData);
    }
    
    const sanitized: any = {};
    for (const [key, value] of Object.entries(data)) {
      const lowerKey = key.toLowerCase();
      if (sensitiveKeys.some(sensitive => lowerKey.includes(sensitive))) {
        sanitized[key] = '[REDACTED]';
      } else {
        sanitized[key] = sanitizeData(value);
      }
    }
    return sanitized;
  }
  
  return data;
}

export const logger = {
  log: (...args: any[]) => {
    if (isDevelopment || enableProductionLogging) {
      const sanitizedArgs = args.map(sanitizeData);
      console.log(...sanitizedArgs);
    }
  },
  
  error: (...args: any[]) => {
    // Sanitize errors in production but still log them
    const sanitizedArgs = args.map(arg => {
      if (arg instanceof Error) {
        // Preserve error structure but sanitize message
        return {
          name: arg.name,
          message: sanitizeData(arg.message),
          stack: isDevelopment ? arg.stack : '[REDACTED]'
        };
      }
      return sanitizeData(arg);
    });
    console.error(...sanitizedArgs);
  },
  
  warn: (...args: any[]) => {
    if (isDevelopment || enableProductionLogging) {
      const sanitizedArgs = args.map(sanitizeData);
      console.warn(...sanitizedArgs);
    }
  },
  
  info: (...args: any[]) => {
    // Sanitize info logs in production
    const sanitizedArgs = args.map(sanitizeData);
    console.info(...sanitizedArgs);
  },
  
  debug: (...args: any[]) => {
    if (isDevelopment) {
      console.debug(...args);
    }
  },
  
  // Group logging for better organization in development
  group: (label: string) => {
    if (isDevelopment) {
      console.group(label);
    }
  },
  
  groupEnd: () => {
    if (isDevelopment) {
      console.groupEnd();
    }
  },
  
  // Table logging for structured data
  table: (data: any) => {
    if (isDevelopment) {
      console.table(data);
    }
  }
};

// Export a development-only logger for verbose debugging
export const devLogger = {
  log: (...args: any[]) => {
    if (isDevelopment) {
      console.log('[DEV]', ...args);
    }
  },
  error: (...args: any[]) => {
    if (isDevelopment) {
      console.error('[DEV]', ...args);
    }
  },
  warn: (...args: any[]) => {
    if (isDevelopment) {
      console.warn('[DEV]', ...args);
    }
  },
  info: (...args: any[]) => {
    if (isDevelopment) {
      console.info('[DEV]', ...args);
    }
  }
};
